import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { CreateReviewRequest, ReviewResponse } from "../../types/reviewTypes";
import type { GigIssueScope, ResolvedGigIssueStatus } from "../../types/resolvedIssueTypes";
import { formatGigCompensation } from "../../utils/GigCompensationUtils";
import { formatDate, formatDateTime } from "../../utils/DateUtils";
import Button from "../ui/Button/Button";
import Modal from "../ui/Modal/Modal";
import PageActionBar from "../ui/Page/PageActionBar";
import PageBackButton from "../ui/Page/PageBackButton";
import PageHero from "../ui/Page/PageHero";
import PageSectionCard from "../ui/Page/PageSectionCard";
import PageShell from "../ui/Page/PageShell";
import Toast, { type ToastTone } from "../ui/Toast/Toast";
import type { GigRequestPaymentStatus } from "../../types/gigTypes";

type RatingFilter = "all" | "5" | "4+" | "3+";
type ReceivedSortOption = "newest" | "oldest" | "highest" | "lowest";
type CompletedStatusFilter = "all" | "needs_review" | "reviewed";
type CompletedSortOption = "recent_completion" | "oldest_completion" | "needs_review_first";
type ResolvedStatusFilter = "all" | "paid_out" | "refunded";
type ResolvedSortOption = "newest_resolved" | "oldest_resolved";
type CancelledStatusFilter = "all" | "refunded" | "not_refunded";
type CancelledSortOption = "newest_cancelled" | "oldest_cancelled";
type ActiveTab = "received" | "completed" | "resolved" | "cancelled";
type ToastState = { message: string; tone: ToastTone } | null;

const resolvedIssueOutcomeClassName: Record<ResolvedGigIssueStatus, string> = {
  RESOLVED_RELEASE_PAYOUT: "border-emerald-200 bg-emerald-100 text-emerald-900",
  RESOLVED_SETTLEMENT: "border-emerald-200 bg-emerald-100 text-emerald-900",
  RESOLVED_REFUND_ADULT: "border-slate-200 bg-slate-100 text-slate-700",
  RESOLVED_REFUND_ADULT_WITH_LATE_FEE: "border-amber-200 bg-amber-100 text-amber-900",
  RESOLVED_PREPARATION_PAYOUT_AND_REFUND: "border-emerald-200 bg-emerald-100 text-emerald-900",
  RESOLVED_TEEN_REMOVED_KEEP_FUNDED: "border-emerald-200 bg-emerald-100 text-emerald-900",
};

const resolvedIssueOutcomeLabel: Record<ResolvedGigIssueStatus, string> = {
  RESOLVED_RELEASE_PAYOUT: "Paid out",
  RESOLVED_SETTLEMENT: "Settled",
  RESOLVED_REFUND_ADULT: "Refunded",
  RESOLVED_REFUND_ADULT_WITH_LATE_FEE: "Refunded less fee",
  RESOLVED_PREPARATION_PAYOUT_AND_REFUND: "Preparation payout",
  RESOLVED_TEEN_REMOVED_KEEP_FUNDED: "Teen removed",
};

const resolvedIssueScopeLabel: Record<GigIssueScope, string> = {
  TEEN_ASSIGNMENT: "Teen assignment",
  WHOLE_GIG: "Whole gig",
};

export type CombinedCompletedGigItem = {
  requestId: string;
  gigId: string | null;
  gigTitle: string | null;
  paymentAmountCents: number | null;
  volunteer: boolean;
  teensRequired: number;
  coveredSeats: number;
  completedAt: string | null;
  counterpartName: string | null;
  submittedReview: ReviewResponse | null;
  reviewStatus: "needs_review" | "reviewed";
};

export type CombinedResolvedIssueItem = {
  issueId: string;
  gigId: string | null;
  gigTitle: string | null;
  counterpartName: string | null;
  reportedBySelf: boolean;
  issueScope: GigIssueScope;
  coveredSeats: number | null;
  status: ResolvedGigIssueStatus;
  resolvedAt: string | null;
  resolutionReason: string | null;
};

export type CombinedCancelledGigItem = {
  requestId: string;
  gigId: string | null;
  gigTitle: string | null;
  paymentAmountCents: number | null;
  volunteer: boolean;
  teensRequired: number;
  coveredSeats: number;
  paymentStatus: GigRequestPaymentStatus | null;
  cancelledAt: string | null;
  refundedAt: string | null;
  counterpartName: string | null;
  cancelledBySelf: boolean;
};

type WorkspaceCopy = {
  eyebrow: string;
  title: string;
  intro: string;
  receivedLabel: string;
  completedLabel: string;
  needsReviewLabel: string;
  resolvedLabel: string;
  cancelledLabel: string;
  completedSummary: string;
  needsReviewSummary: string;
  resolvedSummary: string;
  cancelledSummary: string;
  completedTabLabel: string;
  receivedTabLabel: string;
  resolvedTabLabel: string;
  cancelledTabLabel: string;
  completedTabTitle: string;
  completedTabDescription: string;
  receivedTabTitle: string;
  receivedTabDescription: string;
  resolvedTabTitle: string;
  resolvedTabDescription: string;
  cancelledTabTitle: string;
  cancelledTabDescription: string;
  receivedReviewTitle: string;
  missingReviewCallout: string;
  missingReviewDescription: string;
  reviewSubmittedLabel: string;
  reviewModalTitle: (gig: CombinedCompletedGigItem) => string;
  reviewModalPlaceholder: string;
  counterpartFallbackLabel: string;
};

type CompletedGigsReviewsWorkspaceProps = {
  copy: WorkspaceCopy;
  receivedReviews: ReviewResponse[];
  completedGigs: CombinedCompletedGigItem[];
  resolvedIssues: CombinedResolvedIssueItem[];
  cancelledGigs: CombinedCancelledGigItem[];
  loading: boolean;
  errorMessage: string | null;
  onSubmitReview: (gig: CombinedCompletedGigItem, payload: CreateReviewRequest) => Promise<void>;
  compensationFormatter?: (paymentAmountCents: number | null, volunteer: boolean, fallback?: string) => string;
  renderCompletedGigActions?: (gig: CombinedCompletedGigItem) => ReactNode;
  additionalStats?: Array<{
    label: string;
    value: ReactNode;
    description?: ReactNode;
  }>;
};

const formatAverage = (value: number) => (Number.isFinite(value) ? value.toFixed(1) : "0.0");

const scrollToReviewWorkspaceItem = (id: string, attempt = 0) => {
  const targetElement = document.getElementById(id);
  if (!targetElement) {
    if (attempt < 8) {
      window.setTimeout(() => scrollToReviewWorkspaceItem(id, attempt + 1), 60);
    }
    return;
  }
  targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
};

const matchesRatingFilter = (rating: number, filter: RatingFilter) => {
  if (filter === "all") {
    return true;
  }

  if (filter === "5") {
    return rating >= 5;
  }

  if (filter === "4+") {
    return rating >= 4;
  }

  return rating >= 3;
};

const getAverageRating = (reviews: ReviewResponse[]) => {
  if (reviews.length === 0) {
    return 0;
  }

  const total = reviews.reduce((sum, review) => sum + review.rating, 0);
  return total / reviews.length;
};

const assignmentContext = (gig: { teensRequired: number; coveredSeats: number }) => {
  const teensRequired = Math.max(1, gig.teensRequired || 1);
  const coveredSeats = Math.max(1, gig.coveredSeats || 1);
  if (teensRequired <= 1 && coveredSeats <= 1) {
    return null;
  }
  return `Assignment: ${coveredSeats} of ${teensRequired} teen seat${teensRequired === 1 ? "" : "s"}`;
};

const sortReceivedReviews = (reviews: ReviewResponse[], sort: ReceivedSortOption) => {
  const next = [...reviews];

  next.sort((left, right) => {
    if (sort === "highest") {
      return right.rating - left.rating;
    }

    if (sort === "lowest") {
      return left.rating - right.rating;
    }

    const leftTime = new Date(left.createdAt).getTime();
    const rightTime = new Date(right.createdAt).getTime();

    if (sort === "oldest") {
      return leftTime - rightTime;
    }

    return rightTime - leftTime;
  });

  return next;
};

const sortCompletedGigs = (gigs: CombinedCompletedGigItem[], sort: CompletedSortOption) => {
  const next = [...gigs];

  next.sort((left, right) => {
    if (sort === "needs_review_first" && left.reviewStatus !== right.reviewStatus) {
      return left.reviewStatus === "needs_review" ? -1 : 1;
    }

    const leftTime = left.completedAt ? new Date(left.completedAt).getTime() : 0;
    const rightTime = right.completedAt ? new Date(right.completedAt).getTime() : 0;

    if (sort === "oldest_completion") {
      return leftTime - rightTime;
    }

    return rightTime - leftTime;
  });

  return next;
};

const parseActiveTab = (value: string | null): ActiveTab => {
  if (value === "received" || value === "resolved" || value === "cancelled") {
    return value;
  }

  return "completed";
};

const sortResolvedIssues = (issues: CombinedResolvedIssueItem[], sort: ResolvedSortOption) => {
  const next = [...issues];

  next.sort((left, right) => {
    const leftTime = left.resolvedAt ? new Date(left.resolvedAt).getTime() : 0;
    const rightTime = right.resolvedAt ? new Date(right.resolvedAt).getTime() : 0;

    if (sort === "oldest_resolved") {
      return leftTime - rightTime;
    }

    return rightTime - leftTime;
  });

  return next;
};

const sortCancelledGigs = (gigs: CombinedCancelledGigItem[], sort: CancelledSortOption) => {
  const next = [...gigs];

  next.sort((left, right) => {
    const leftTime = left.cancelledAt ? new Date(left.cancelledAt).getTime() : 0;
    const rightTime = right.cancelledAt ? new Date(right.cancelledAt).getTime() : 0;

    if (sort === "oldest_cancelled") {
      return leftTime - rightTime;
    }

    return rightTime - leftTime;
  });

  return next;
};

const ratingOptions = [5, 4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1, 0.5, 0];
const reviewWorkspaceControlClassName =
  "h-11 w-full min-w-0 rounded-xl border border-myteengig-border bg-white px-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary";
const reviewWorkspaceItemClassName =
  "min-w-0 overflow-hidden rounded-2xl border bg-gradient-to-br p-3 shadow-sm sm:p-5";
const reviewWorkspaceItemHeaderClassName =
  "grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start sm:gap-3";
const reviewWorkspacePillClassName =
  "inline-flex w-fit max-w-full shrink-0 whitespace-normal rounded-full border px-3 py-1 text-left text-xs font-semibold uppercase leading-tight tracking-wide";

const CompletedGigsReviewsWorkspace = ({
  copy,
  receivedReviews,
  completedGigs,
  resolvedIssues,
  cancelledGigs,
  loading,
  errorMessage,
  onSubmitReview,
  compensationFormatter = formatGigCompensation,
  renderCompletedGigActions,
  additionalStats = [],
}: CompletedGigsReviewsWorkspaceProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => parseActiveTab(searchParams.get("tab")));
  const [search, setSearch] = useState("");
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>("all");
  const [receivedSortBy, setReceivedSortBy] = useState<ReceivedSortOption>("newest");
  const [completedStatusFilter, setCompletedStatusFilter] = useState<CompletedStatusFilter>("all");
  const [completedSortBy, setCompletedSortBy] = useState<CompletedSortOption>("needs_review_first");
  const [resolvedStatusFilter, setResolvedStatusFilter] = useState<ResolvedStatusFilter>("all");
  const [resolvedSortBy, setResolvedSortBy] = useState<ResolvedSortOption>("newest_resolved");
  const [cancelledStatusFilter, setCancelledStatusFilter] = useState<CancelledStatusFilter>("all");
  const [cancelledSortBy, setCancelledSortBy] = useState<CancelledSortOption>("newest_cancelled");
  const [selectedGig, setSelectedGig] = useState<CombinedCompletedGigItem | null>(null);
  const [reviewRating, setReviewRating] = useState("5");
  const [reviewMessage, setReviewMessage] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const highlightedReviewId = searchParams.get("reviewId");
  const highlightedIssueId = searchParams.get("issueId");
  const highlightedRequestId = searchParams.get("requestId");

  const normalizedSearch = search.trim().toLowerCase();

  const filteredReceivedReviews = useMemo(
    () =>
      sortReceivedReviews(
        receivedReviews.filter((review) => {
          const reviewerName = [review.reviewerFirstName, review.reviewerLastName].filter(Boolean).join(" ");
          const location = [review.gigCity, review.gigState].filter(Boolean).join(" ");
          const searchText = [review.gigTitle ?? "", review.gigCategory ?? "", location, reviewerName]
            .join(" ")
            .toLowerCase();

          const matchesSearch = normalizedSearch.length === 0 || searchText.includes(normalizedSearch);
          return matchesSearch && matchesRatingFilter(review.rating, ratingFilter);
        }),
        receivedSortBy,
      ),
    [normalizedSearch, ratingFilter, receivedReviews, receivedSortBy],
  );

  const filteredCompletedGigs = useMemo(
    () =>
      sortCompletedGigs(
        completedGigs.filter((gig) => {
          const reviewText = gig.submittedReview?.message ?? "";
          const searchText = [gig.gigTitle ?? "", gig.counterpartName ?? "", reviewText].join(" ").toLowerCase();
          const matchesSearch = normalizedSearch.length === 0 || searchText.includes(normalizedSearch);
          const matchesStatus = completedStatusFilter === "all" || gig.reviewStatus === completedStatusFilter;

          return matchesSearch && matchesStatus;
        }),
        completedSortBy,
      ),
    [completedGigs, completedSortBy, completedStatusFilter, normalizedSearch],
  );

  const filteredResolvedIssues = useMemo(
    () =>
      sortResolvedIssues(
        resolvedIssues.filter((issue) => {
          const searchText = [issue.gigTitle ?? "", issue.counterpartName ?? "", issue.resolutionReason ?? ""].join(" ").toLowerCase();
          const matchesSearch = normalizedSearch.length === 0 || searchText.includes(normalizedSearch);
          const matchesStatus =
            resolvedStatusFilter === "all"
              || (resolvedStatusFilter === "paid_out" && issue.status === "RESOLVED_RELEASE_PAYOUT")
              || (resolvedStatusFilter === "refunded" && (
                issue.status === "RESOLVED_SETTLEMENT"
                || 
                issue.status === "RESOLVED_REFUND_ADULT"
                || issue.status === "RESOLVED_REFUND_ADULT_WITH_LATE_FEE"
                || issue.status === "RESOLVED_PREPARATION_PAYOUT_AND_REFUND"
              ));

          return matchesSearch && matchesStatus;
        }),
        resolvedSortBy,
      ),
    [normalizedSearch, resolvedIssues, resolvedSortBy, resolvedStatusFilter],
  );

  const filteredCancelledGigs = useMemo(
    () =>
      sortCancelledGigs(
        cancelledGigs.filter((gig) => {
          const searchText = [gig.gigTitle ?? "", gig.counterpartName ?? ""].join(" ").toLowerCase();
          const matchesSearch = normalizedSearch.length === 0 || searchText.includes(normalizedSearch);
          const matchesStatus =
            cancelledStatusFilter === "all"
              || (cancelledStatusFilter === "refunded" && gig.paymentStatus === "REFUNDED")
              || (cancelledStatusFilter === "not_refunded" && gig.paymentStatus !== "REFUNDED");

          return matchesSearch && matchesStatus;
        }),
        cancelledSortBy,
      ),
    [cancelledGigs, cancelledSortBy, cancelledStatusFilter, normalizedSearch],
  );

  const needsReviewCount = completedGigs.filter((gig) => gig.reviewStatus === "needs_review").length;
  const reviewedCount = completedGigs.length - needsReviewCount;
  const shouldScrollCompletedGrid = filteredCompletedGigs.length > 4;
  const shouldScrollReceivedGrid = filteredReceivedReviews.length > 4;
  const shouldScrollResolvedGrid = filteredResolvedIssues.length > 4;
  const shouldScrollCancelledGrid = filteredCancelledGigs.length > 4;
  const selectedGigAssignmentContext = selectedGig ? assignmentContext(selectedGig) : null;

  useEffect(() => {
    const nextTab = parseActiveTab(searchParams.get("tab"));
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, searchParams]);

  useEffect(() => {
    if (activeTab !== "received" || !highlightedReviewId || loading) {
      return;
    }

    scrollToReviewWorkspaceItem(`received-review-${highlightedReviewId}`);
  }, [activeTab, highlightedReviewId, loading, filteredReceivedReviews]);

  useEffect(() => {
    if (activeTab !== "resolved" || !highlightedIssueId || loading) {
      return;
    }

    scrollToReviewWorkspaceItem(`resolved-issue-${highlightedIssueId}`);
  }, [activeTab, highlightedIssueId, loading, filteredResolvedIssues]);

  useEffect(() => {
    if (activeTab !== "completed" || !highlightedRequestId || loading) {
      return;
    }

    scrollToReviewWorkspaceItem(`completed-gig-${highlightedRequestId}`);
  }, [activeTab, highlightedRequestId, loading, filteredCompletedGigs]);

  useEffect(() => {
    if (activeTab !== "cancelled" || !highlightedRequestId || loading) {
      return;
    }

    scrollToReviewWorkspaceItem(`cancelled-gig-${highlightedRequestId}`);
  }, [activeTab, highlightedRequestId, loading, filteredCancelledGigs]);

  const handleTabChange = (tab: ActiveTab) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    if (tab === "completed") {
      nextSearchParams.delete("tab");
    } else {
      nextSearchParams.set("tab", tab);
    }
    setSearchParams(nextSearchParams);
    setActiveTab(tab);
  };

  const openReviewModal = (gig: CombinedCompletedGigItem) => {
    setSelectedGig(gig);
    setReviewRating("5");
    setReviewMessage("");
    setSubmissionError(null);
  };

  const closeReviewModal = () => {
    if (submittingReview) {
      return;
    }

    setSelectedGig(null);
    setReviewRating("5");
    setReviewMessage("");
    setSubmissionError(null);
  };

  const handleSubmitReview = async () => {
    if (!selectedGig) {
      return;
    }

    setSubmittingReview(true);
    setSubmissionError(null);

    try {
      await onSubmitReview(selectedGig, {
        rating: Number(reviewRating),
        message: reviewMessage.trim().length > 0 ? reviewMessage.trim() : null,
      } as CreateReviewRequest);

      setSelectedGig(null);
      setReviewRating("5");
      setReviewMessage("");
      setToast({ tone: "success", message: "Review submitted for the completed gig." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "We couldn't submit your review.";
      setSubmissionError(message);
      setToast({ tone: "error", message: "Review submission failed." });
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <PageShell width="6xl">
      <Toast
        isOpen={toast !== null}
        tone={toast?.tone ?? "info"}
        message={toast?.message ?? ""}
        onClose={() => setToast(null)}
      />
      <PageBackButton fallbackTo="/dashboard" label="Back to Dashboard" className="mb-4" />

      <PageHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.intro}
        stats={[
          {
            label: copy.receivedLabel,
            value: receivedReviews.length,
            description: `Average rating ${formatAverage(getAverageRating(receivedReviews))} / 5`,
          },
          {
            label: copy.completedLabel,
            value: completedGigs.length,
            description: copy.completedSummary.replace("{reviewedCount}", reviewedCount.toString()),
          },
          {
            label: copy.needsReviewLabel,
            value: needsReviewCount,
            description: copy.needsReviewSummary,
          },
          {
            label: copy.resolvedLabel,
            value: resolvedIssues.length,
            description: copy.resolvedSummary.replace("{resolvedCount}", resolvedIssues.length.toString()),
          },
          {
            label: copy.cancelledLabel,
            value: cancelledGigs.length,
            description: copy.cancelledSummary.replace("{cancelledCount}", cancelledGigs.length.toString()),
          },
          ...additionalStats,
        ]}
        statClassName="min-w-0 bg-white/90"
      />

      {errorMessage ? (
        <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{errorMessage}</div>
      ) : null}

      <PageSectionCard className="mt-6 min-w-0 overflow-hidden p-3 sm:p-5">
        <PageActionBar align="center">
          <Button
            variant={activeTab === "completed" ? "primary" : "outline"}
            className="w-full whitespace-normal text-center leading-tight sm:min-w-[200px] sm:w-auto"
            onClick={() => handleTabChange("completed")}
          >
            {copy.completedTabLabel}
          </Button>
          <Button
            variant={activeTab === "received" ? "primary" : "outline"}
            className="w-full whitespace-normal text-center leading-tight sm:min-w-[200px] sm:w-auto"
            onClick={() => handleTabChange("received")}
          >
            {copy.receivedTabLabel}
          </Button>
          <Button
            variant={activeTab === "resolved" ? "primary" : "outline"}
            className="w-full whitespace-normal text-center leading-tight sm:min-w-[200px] sm:w-auto"
            onClick={() => handleTabChange("resolved")}
          >
            {copy.resolvedTabLabel}
          </Button>
          <Button
            variant={activeTab === "cancelled" ? "primary" : "outline"}
            className="w-full whitespace-normal text-center leading-tight sm:min-w-[200px] sm:w-auto"
            onClick={() => handleTabChange("cancelled")}
          >
            {copy.cancelledTabLabel}
          </Button>
        </PageActionBar>

        <div className="mt-5 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background/60 px-3 py-3 text-center sm:px-4">
          <p className="text-sm font-semibold text-myteengig-text-primary">
            {activeTab === "completed"
              ? copy.completedTabTitle
              : activeTab === "received"
                ? copy.receivedTabTitle
                : activeTab === "resolved"
                  ? copy.resolvedTabTitle
                  : copy.cancelledTabTitle}
          </p>
          <p className="text-sm text-myteengig-text-muted">
            {activeTab === "completed"
              ? copy.completedTabDescription
              : activeTab === "received"
                ? copy.receivedTabDescription
                : activeTab === "resolved"
                  ? copy.resolvedTabDescription
                  : copy.cancelledTabDescription}
          </p>
        </div>

        <div className="mx-auto mt-5 max-w-5xl overflow-hidden rounded-2xl border border-myteengig-border bg-gradient-to-b from-white to-myteengig-background/60 p-3 sm:p-4">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-myteengig-text-muted">
            {activeTab === "completed"
              ? "Filter Completed Gigs"
              : activeTab === "received"
                ? "Filter Reviews"
                : activeTab === "resolved"
                  ? "Filter Resolved Issues"
                  : "Filter Cancelled Gigs"}
          </p>
          {activeTab === "completed" ? (
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Search completed gigs
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by gig title, person, or notes"
                  className={reviewWorkspaceControlClassName}
                />
              </label>
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Review status
                <select
                  value={completedStatusFilter}
                  onChange={(event) => setCompletedStatusFilter(event.target.value as CompletedStatusFilter)}
                  className={reviewWorkspaceControlClassName}
                >
                  <option value="all">All completed gigs</option>
                  <option value="needs_review">Review needed</option>
                  <option value="reviewed">Reviewed</option>
                </select>
              </label>
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Sort by
                <select
                  value={completedSortBy}
                  onChange={(event) => setCompletedSortBy(event.target.value as CompletedSortOption)}
                  className={reviewWorkspaceControlClassName}
                >
                  <option value="needs_review_first">Review needed first</option>
                  <option value="recent_completion">Most recently completed</option>
                  <option value="oldest_completion">Oldest completion first</option>
                </select>
              </label>
            </div>
          ) : activeTab === "received" ? (
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Search reviews
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by title, category, location, or name"
                  className={reviewWorkspaceControlClassName}
                />
              </label>
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Minimum rating
                <select
                  value={ratingFilter}
                  onChange={(event) => setRatingFilter(event.target.value as RatingFilter)}
                  className={reviewWorkspaceControlClassName}
                >
                  <option value="all">All ratings</option>
                  <option value="5">5.0 only</option>
                  <option value="4+">4.0 and up</option>
                  <option value="3+">3.0 and up</option>
                </select>
              </label>
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Sort by
                <select
                  value={receivedSortBy}
                  onChange={(event) => setReceivedSortBy(event.target.value as ReceivedSortOption)}
                  className={reviewWorkspaceControlClassName}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="highest">Highest rating</option>
                  <option value="lowest">Lowest rating</option>
                </select>
              </label>
            </div>
          ) : activeTab === "resolved" ? (
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Search resolved issues
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by gig title, person, or reason"
                  className={reviewWorkspaceControlClassName}
                />
              </label>
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Outcome
                <select
                  value={resolvedStatusFilter}
                  onChange={(event) => setResolvedStatusFilter(event.target.value as ResolvedStatusFilter)}
                  className={reviewWorkspaceControlClassName}
                >
                  <option value="all">All outcomes</option>
                  <option value="paid_out">Paid out after review</option>
                  <option value="refunded">Refunded after review</option>
                </select>
              </label>
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Sort by
                <select
                  value={resolvedSortBy}
                  onChange={(event) => setResolvedSortBy(event.target.value as ResolvedSortOption)}
                  className={reviewWorkspaceControlClassName}
                >
                  <option value="newest_resolved">Most recently resolved</option>
                  <option value="oldest_resolved">Oldest resolved first</option>
                </select>
              </label>
            </div>
          ) : (
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Search cancelled gigs
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by gig title or person"
                  className={reviewWorkspaceControlClassName}
                />
              </label>
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Refund status
                <select
                  value={cancelledStatusFilter}
                  onChange={(event) => setCancelledStatusFilter(event.target.value as CancelledStatusFilter)}
                  className={reviewWorkspaceControlClassName}
                >
                  <option value="all">All cancelled gigs</option>
                  <option value="refunded">Refunded</option>
                  <option value="not_refunded">Not refunded</option>
                </select>
              </label>
              <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                Sort by
                <select
                  value={cancelledSortBy}
                  onChange={(event) => setCancelledSortBy(event.target.value as CancelledSortOption)}
                  className={reviewWorkspaceControlClassName}
                >
                  <option value="newest_cancelled">Most recently cancelled</option>
                  <option value="oldest_cancelled">Oldest cancelled first</option>
                </select>
              </label>
            </div>
          )}
        </div>

        {loading ? (
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-48 animate-pulse rounded-2xl border border-myteengig-border bg-myteengig-background" />
            ))}
          </div>
        ) : null}

        {!loading && activeTab === "completed" && filteredCompletedGigs.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background p-4 text-center sm:p-8">
            <p className="text-lg font-semibold text-myteengig-text-primary">No completed gigs match those filters.</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">Try clearing the search or switching the review status filter.</p>
          </div>
        ) : null}

        {!loading && activeTab === "received" && filteredReceivedReviews.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background p-4 text-center sm:p-8">
            <p className="text-lg font-semibold text-myteengig-text-primary">No reviews match those filters.</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">Try clearing the search or lowering the minimum rating filter.</p>
          </div>
        ) : null}

        {!loading && activeTab === "resolved" && filteredResolvedIssues.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background p-4 text-center sm:p-8">
            <p className="text-lg font-semibold text-myteengig-text-primary">No resolved issues match those filters.</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">Try clearing the search or switching the outcome filter.</p>
          </div>
        ) : null}

        {!loading && activeTab === "cancelled" && filteredCancelledGigs.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background p-4 text-center sm:p-8">
            <p className="text-lg font-semibold text-myteengig-text-primary">No cancelled gigs match those filters.</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">Try clearing the search or switching the refund-status filter.</p>
          </div>
        ) : null}

        {!loading && activeTab === "completed" && filteredCompletedGigs.length > 0 ? (
          <div className={shouldScrollCompletedGrid ? "mt-6 max-h-[58rem] overflow-y-auto pr-0 sm:pr-2" : "mt-6"}>
            <div className="grid min-w-0 gap-4 lg:grid-cols-2">
              {filteredCompletedGigs.map((gig) => {
                const badgeClasses =
                  gig.reviewStatus === "needs_review"
                    ? "border-amber-200 bg-amber-100 text-amber-900"
                    : "border-emerald-200 bg-emerald-100 text-emerald-900";
                const completedGigActions = renderCompletedGigActions?.(gig);
                const isHighlighted = gig.requestId === highlightedRequestId;
                const completedAssignmentContext = assignmentContext(gig);

                return (
                  <article
                    key={gig.requestId}
                    id={`completed-gig-${gig.requestId}`}
                    className={`flex h-full flex-col ${reviewWorkspaceItemClassName} ${
                      isHighlighted
                        ? "border-myteengig-primary ring-2 ring-myteengig-primary/25 from-myteengig-primary/10 to-white"
                        : "border-myteengig-border from-white to-myteengig-background/60"
                    }`}
                  >
                    <div className={reviewWorkspaceItemHeaderClassName}>
                      <div className="min-w-0">
                        <h2 className="break-words text-base font-semibold leading-6 text-myteengig-text-primary sm:text-lg">{gig.gigTitle ?? "Completed gig"}</h2>
                        <p className="mt-1 text-sm text-myteengig-text-muted">
                          {gig.counterpartName || copy.counterpartFallbackLabel}
                        </p>
                        {completedAssignmentContext ? (
                          <p className="mt-1 text-xs font-medium text-myteengig-text-muted">{completedAssignmentContext}</p>
                        ) : null}
                      </div>
                      <span className={`${reviewWorkspacePillClassName} ${badgeClasses}`}>
                        {gig.reviewStatus === "needs_review" ? "Review needed" : "Reviewed"}
                      </span>
                    </div>

                    <div className="mt-4 grid gap-3 text-sm text-myteengig-text-muted sm:grid-cols-2">
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/85 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Completed</p>
                        <p className="mt-1 break-words">{formatDateTime(gig.completedAt, "Not available")}</p>
                      </div>
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/85 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Compensation</p>
                        <p className="mt-1 break-words">
                          {compensationFormatter(gig.paymentAmountCents, gig.volunteer)}
                        </p>
                      </div>
                    </div>

                    {gig.submittedReview ? (
                      <div className="mt-4 min-w-0 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-3 sm:p-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-900">{copy.reviewSubmittedLabel}</p>
                          <span className="w-fit rounded-full border border-emerald-200 bg-white px-3 py-1 text-sm font-semibold text-emerald-900">
                            {gig.submittedReview.rating.toFixed(1)} / 5
                          </span>
                        </div>
                        <p className="mt-2 break-words text-sm text-emerald-900/80">Submitted {formatDate(gig.submittedReview.createdAt)}</p>
                        <p className="mt-3 break-words text-sm leading-6 text-myteengig-text-muted">
                          {gig.submittedReview.message?.trim()
                            ? gig.submittedReview.message
                            : "No written message was included with this review."}
                        </p>
                      </div>
                    ) : (
                      <div className="mt-4 min-w-0 rounded-2xl border border-dashed border-amber-300 bg-amber-50/70 p-3 sm:p-4">
                        <p className="text-sm font-semibold text-amber-900">{copy.missingReviewCallout}</p>
                        <p className="mt-2 break-words text-sm text-amber-900/80">{copy.missingReviewDescription}</p>
                      </div>
                    )}

                    <PageActionBar align="end" className="mt-auto pt-4">
                      {completedGigActions}
                      {gig.reviewStatus === "needs_review" ? (
                        <Button onClick={() => openReviewModal(gig)} className="w-full sm:w-auto">Leave review</Button>
                      ) : (
                        <Button variant="outline" disabled className="w-full sm:w-auto">
                          Review submitted
                        </Button>
                      )}
                    </PageActionBar>
                  </article>
                );
              })}
            </div>
          </div>
        ) : null}

        {!loading && activeTab === "received" && filteredReceivedReviews.length > 0 ? (
          <div className={shouldScrollReceivedGrid ? "mt-6 max-h-[58rem] overflow-y-auto pr-0 sm:pr-2" : "mt-6"}>
            <div className="grid min-w-0 gap-4 lg:grid-cols-2">
              {filteredReceivedReviews.map((review) => {
                const reviewerName = [review.reviewerFirstName, review.reviewerLastName].filter(Boolean).join(" ");
                const location = [review.gigCity, review.gigState].filter(Boolean).join(", ");
                const isHighlighted = review.id === highlightedReviewId;

                return (
                  <article
                    key={review.id}
                    id={`received-review-${review.id}`}
                    className={`${reviewWorkspaceItemClassName} transition ${
                      isHighlighted
                        ? "border-myteengig-primary ring-2 ring-myteengig-primary/25 from-myteengig-primary/10 to-white"
                        : "border-myteengig-primary/20 from-white to-myteengig-primary/5"
                    }`}
                  >
                    <div className={reviewWorkspaceItemHeaderClassName}>
                      <div className="min-w-0">
                        <h2 className="break-words text-base font-semibold leading-6 text-myteengig-text-primary sm:text-lg">{copy.receivedReviewTitle}</h2>
                        <p className="mt-1 break-words text-sm text-myteengig-text-muted">{review.gigTitle ?? "Completed gig"}</p>
                      </div>
                      <span className="inline-flex w-fit max-w-full shrink-0 whitespace-normal rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-left text-sm font-semibold leading-tight text-amber-900">
                        {review.rating.toFixed(1)} / 5
                      </span>
                    </div>

                    <div className="mt-4 grid gap-3 text-sm text-myteengig-text-muted sm:grid-cols-2">
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/80 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Reviewed by</p>
                        <p className="mt-1 break-words">{reviewerName || "Not available"}</p>
                      </div>
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/80 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Date</p>
                        <p className="mt-1 break-words">{formatDate(review.createdAt)}</p>
                      </div>
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/80 px-3 py-2 sm:col-span-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Location</p>
                        <p className="mt-1 break-words">{location || "Not available"}</p>
                      </div>
                    </div>

                    <div className="mt-4 min-w-0 rounded-2xl border border-myteengig-border bg-white/85 p-3 sm:p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Written feedback</p>
                      <p className="mt-2 break-words text-sm leading-6 text-myteengig-text-muted">
                        {review.message?.trim() ? review.message : "No written message was included with this rating."}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        ) : null}

        {!loading && activeTab === "resolved" && filteredResolvedIssues.length > 0 ? (
          <div className={shouldScrollResolvedGrid ? "mt-6 max-h-[58rem] overflow-y-auto pr-0 sm:pr-2" : "mt-6"}>
            <div className="grid min-w-0 gap-4 lg:grid-cols-2">
              {filteredResolvedIssues.map((issue) => {
                const outcomeClassName = resolvedIssueOutcomeClassName[issue.status];
                const isHighlighted = issue.issueId === highlightedIssueId;

                return (
                  <article
                    key={issue.issueId}
                    id={`resolved-issue-${issue.issueId}`}
                    className={`${reviewWorkspaceItemClassName} ${
                      isHighlighted
                        ? "border-myteengig-primary ring-2 ring-myteengig-primary/25 from-myteengig-primary/10 to-white"
                        : "border-myteengig-border from-white to-myteengig-background/60"
                    }`}
                  >
                    <div className={reviewWorkspaceItemHeaderClassName}>
                      <div className="min-w-0">
                        <h2 className="break-words text-base font-semibold leading-6 text-myteengig-text-primary sm:text-lg">{issue.gigTitle ?? "Resolved issue"}</h2>
                        <p className="mt-1 text-sm text-myteengig-text-muted">
                          {issue.counterpartName || copy.counterpartFallbackLabel}
                        </p>
                      </div>
                      <span className={`${reviewWorkspacePillClassName} ${outcomeClassName}`}>
                        {resolvedIssueOutcomeLabel[issue.status]}
                      </span>
                    </div>

                    <div className="mt-4 grid gap-3 text-sm text-myteengig-text-muted sm:grid-cols-2">
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/85 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Resolved</p>
                        <p className="mt-1 break-words">{formatDateTime(issue.resolvedAt, "Not available")}</p>
                      </div>
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/85 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Issue opened by</p>
                        <p className="mt-1 break-words">{issue.reportedBySelf ? "You" : "Other party"}</p>
                      </div>
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/85 px-3 py-2 sm:col-span-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Scope</p>
                        <p className="mt-1 break-words">
                          {resolvedIssueScopeLabel[issue.issueScope]}
                          {issue.coveredSeats && issue.coveredSeats > 1 ? `, ${issue.coveredSeats} seats` : ""}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 min-w-0 rounded-2xl border border-myteengig-border bg-white/85 p-3 sm:p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Resolution summary</p>
                      <p className="mt-2 break-words text-sm leading-6 text-myteengig-text-muted">
                        {issue.resolutionReason?.trim() ? issue.resolutionReason : "Support closed this issue without an added written summary."}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        ) : null}

        {!loading && activeTab === "cancelled" && filteredCancelledGigs.length > 0 ? (
          <div className={shouldScrollCancelledGrid ? "mt-6 max-h-[58rem] overflow-y-auto pr-0 sm:pr-2" : "mt-6"}>
            <div className="grid min-w-0 gap-4 lg:grid-cols-2">
              {filteredCancelledGigs.map((gig) => {
                const statusLabel = gig.paymentStatus === "REFUNDED" ? "Refunded" : "Cancelled";
                const badgeClassName =
                  gig.paymentStatus === "REFUNDED"
                    ? "border-slate-200 bg-slate-100 text-slate-700"
                    : "border-amber-200 bg-amber-100 text-amber-900";
                const isHighlighted = gig.requestId === highlightedRequestId;
                const cancelledAssignmentContext = assignmentContext(gig);

                return (
                  <article
                    key={gig.requestId}
                    id={`cancelled-gig-${gig.requestId}`}
                    className={`${reviewWorkspaceItemClassName} ${
                      isHighlighted
                        ? "border-myteengig-primary ring-2 ring-myteengig-primary/25 from-myteengig-primary/10 to-white"
                        : "border-myteengig-border from-white to-myteengig-background/60"
                    }`}
                  >
                    <div className={reviewWorkspaceItemHeaderClassName}>
                      <div className="min-w-0">
                        <h2 className="break-words text-base font-semibold leading-6 text-myteengig-text-primary sm:text-lg">{gig.gigTitle ?? "Cancelled gig"}</h2>
                        <p className="mt-1 text-sm text-myteengig-text-muted">
                          {gig.counterpartName || copy.counterpartFallbackLabel}
                        </p>
                        {cancelledAssignmentContext ? (
                          <p className="mt-1 text-xs font-medium text-myteengig-text-muted">{cancelledAssignmentContext}</p>
                        ) : null}
                      </div>
                      <span className={`${reviewWorkspacePillClassName} ${badgeClassName}`}>
                        {statusLabel}
                      </span>
                    </div>

                    <div className="mt-4 grid gap-3 text-sm text-myteengig-text-muted sm:grid-cols-2">
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/85 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Cancelled</p>
                        <p className="mt-1 break-words">{formatDateTime(gig.cancelledAt, "Not available")}</p>
                      </div>
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/85 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Compensation</p>
                        <p className="mt-1 break-words">{compensationFormatter(gig.paymentAmountCents, gig.volunteer)}</p>
                      </div>
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/85 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Cancelled by</p>
                        <p className="mt-1 break-words">{gig.cancelledBySelf ? "You" : "Other party"}</p>
                      </div>
                      <div className="min-w-0 rounded-xl border border-myteengig-border bg-white/85 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">Refund status</p>
                        <p className="mt-1 break-words">{gig.refundedAt ? `Refunded ${formatDateTime(gig.refundedAt, "Not available")}` : "No refund recorded"}</p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        ) : null}
      </PageSectionCard>

      <Modal isOpen={selectedGig !== null} onClose={closeReviewModal} title={selectedGig ? copy.reviewModalTitle(selectedGig) : "Leave review"}>
        {selectedGig ? (
          <div className="space-y-5">
            {submissionError ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{submissionError}</div>
            ) : null}

            <div className="min-w-0 rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-3 sm:p-4">
              <p className="break-words text-sm font-semibold text-myteengig-text-primary">{selectedGig.gigTitle ?? "Completed gig"}</p>
              <p className="mt-1 break-words text-sm text-myteengig-text-muted">{selectedGig.counterpartName || copy.counterpartFallbackLabel}</p>
              {selectedGigAssignmentContext ? (
                <p className="mt-1 break-words text-sm text-myteengig-text-muted">{selectedGigAssignmentContext}</p>
              ) : null}
              <p className="mt-1 break-words text-sm text-myteengig-text-muted">Completed {formatDateTime(selectedGig.completedAt, "Not available")}</p>
            </div>

            <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
              Rating
              <select
                value={reviewRating}
                onChange={(event) => setReviewRating(event.target.value)}
                className={reviewWorkspaceControlClassName}
              >
                {ratingOptions.map((option) => (
                  <option key={option} value={option.toString()}>
                    {option.toFixed(1)} / 5
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
              Written feedback
              <textarea
                value={reviewMessage}
                onChange={(event) => setReviewMessage(event.target.value)}
                rows={5}
                placeholder={copy.reviewModalPlaceholder}
                className="w-full min-w-0 rounded-xl border border-myteengig-border bg-white px-3 py-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary"
              />
            </label>

            <PageActionBar align="end">
              <Button variant="outline" onClick={closeReviewModal} disabled={submittingReview} className="w-full sm:w-auto">
                Skip for now
              </Button>
              <Button onClick={handleSubmitReview} disabled={submittingReview} className="w-full sm:w-auto">
                {submittingReview ? "Submitting..." : "Submit review"}
              </Button>
            </PageActionBar>
          </div>
        ) : null}
      </Modal>
    </PageShell>
  );
};

export default CompletedGigsReviewsWorkspace;
